import React, { useRef } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/BucksLanding.css';
import coverPhotoMobile from '../imgs/optimized/front_BucksCoverPhoto-360.webp';
import coverPhotoDesktop from '../imgs/optimized/front_BucksCoverPhoto-720.webp';
import coverPhotoFallback from '../imgs/front_BucksCoverPhoto.jpg';
import bucksBoxImageMobile from '../imgs/optimized/bucksBoxImage-360.webp';
import bucksBoxImageDesktop from '../imgs/optimized/bucksBoxImage-720.webp';
import bucksBoxImageFallback from '../imgs/bucksBoxImage.png';
import bucksStoryImageMobile from '../imgs/optimized/bucksLandingPageOurStory-360.webp';
import bucksStoryImageDesktop from '../imgs/optimized/bucksLandingPageOurStory-500.webp';
import bucksStoryImageFallback from '../imgs/bucksLandingPageOurStory_IMG.png';
import { buildSpecialPricingInfo, calculateSpecialPricing, getUnitLabel } from '../utils/pricing';
import { isProductOutOfStock } from '../utils/inventoryAvailability';

const newsletterFetch = (...args) => {
  const wafFetch = window.AwsWafIntegration?.fetch;
  return typeof wafFetch === 'function' ? wafFetch(...args) : fetch(...args);
};

function BucksLanding() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // New Product States
  const [seasonalProducts, setSeasonalProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productError, setProductError] = useState(null);
  
  // Newsletter subscription states
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);
  const [newsletterFormStartedAt, setNewsletterFormStartedAt] = useState(() => Date.now());
  const [newsletterCompanyUrl, setNewsletterCompanyUrl] = useState('');
  
  // Add these new state variables for quantity input functionality
  const [quantityInputMode, setQuantityInputMode] = useState({}); // Track which products are in input mode
  const [quantityValues, setQuantityValues] = useState({}); // Track quantity values for each product
  const [shouldLoadAnnouncements, setShouldLoadAnnouncements] = useState(false);
  const [shouldLoadProducts, setShouldLoadProducts] = useState(false);
  const announcementSectionRef = useRef(null);
  const featuredSectionRef = useRef(null);
  
  // Get the cart context functions
  const { addToCart } = useCart();

  // Customer testimonials pulled from published Google reviews
  const testimonials = [
    {
      quote: "Tiny roadside stand was surprisingly well stocked and had almost everything I was looking for. Produce was peak ripeness and delicious, and staff has been friendly and helpful each visit. Highly recommend!",
      author: "Lyssa Schwarz, Google Review"
    },
    {
      quote: "Great place, fresh produce, decent prices.",
      author: "Martin Cusack, Google Review"
    },
    {
      quote: "This is such a nice family owned farm stand! They have a wide variety of local produce at great prices. The fruits and vegetables are always fresh and the owner and staff are the best.",
      author: "Jen Huang, Google Review"
    }
  ];

  // Enhanced notification function to match shop page - DEFINED FIRST
  const showNotification = useCallback((message, type = 'success') => {
    const notification = document.getElementById('cart-notification');
    if (notification) {
      notification.textContent = message;
      notification.classList.add('show');
      if (type === 'error') {
        notification.classList.add('error');
      }
      
      setTimeout(() => {
        notification.classList.remove('show', 'error');
      }, type === 'error' ? 5000 : 3000);
    }
  }, []); // Empty dependency array - function is stable

  // Enhanced quantity change handler that allows empty strings temporarily
  const handleQuantityChange = useCallback((productId, value) => {
    // Allow empty string temporarily for better UX
    if (value === '') {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: ''
      }));
      return;
    }
    
    // Parse the value and ensure it's a positive number
    const numValue = parseInt(value, 10);
    
    // Only update if it's a valid positive number
    if (!isNaN(numValue) && numValue > 0) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: numValue
      }));
    }
  }, []);

  // Add this new function to handle when user leaves the input field
  const handleQuantityBlur = useCallback((productId) => {
    const currentValue = quantityValues[productId];
    
    // If empty or invalid, set to 1
    if (currentValue === '' || currentValue === null || currentValue === undefined || currentValue < 1) {
      setQuantityValues(prev => ({
        ...prev,
        [productId]: 1
      }));
    }
  }, [quantityValues]);

  // Add this function to handle input focus (select all text)
  const handleQuantityFocus = useCallback((event) => {
    // Select all text when input is focused for easier editing
    event.target.select();
  }, []);

  // Add this function to cancel quantity input mode
  const handleCancelQuantityInput = useCallback((productId, event) => {
    if (event) {
      event.stopPropagation();
    }
    setQuantityInputMode(prev => ({
      ...prev,
      [productId]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [productId]: 1
    }));
  }, []);

  const normalizeProductId = useCallback((rawId) => {
    if (typeof rawId === 'number') {
      return rawId;
    }

    if (typeof rawId !== 'string') {
      return rawId;
    }

    const trimmed = rawId.trim();
    if (/^\d+$/.test(trimmed)) {
      return parseInt(trimmed, 10);
    }

    // Backward compatibility for landing API IDs like "featured-123".
    const featuredMatch = trimmed.match(/^featured-(\d+)$/i);
    if (featuredMatch) {
      return parseInt(featuredMatch[1], 10);
    }

    return rawId;
  }, []);

  // Enhanced add to cart handler with proper corn identification and consistent product structure
  const handleAddToCart = useCallback((product, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // If not in quantity input mode, show the input
    if (!quantityInputMode[product.id]) {
      setQuantityInputMode(prev => ({
        ...prev,
        [product.id]: true
      }));
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1 // Default to 1
      }));
      return;
    }
    
    // Ensure we have a valid quantity before adding to cart
    let quantity = quantityValues[product.id];
    if (quantity === '' || quantity === null || quantity === undefined || quantity < 1) {
      quantity = 1;
      // Update the state to reflect the corrected value
      setQuantityValues(prev => ({
        ...prev,
        [product.id]: 1
      }));
    }
    
    const specialPricing = calculateSpecialPricing(product, quantity);
    
    // Ensure consistent product structure - normalize the product
    const cartProduct = { 
      // Normalize IDs from all inventory endpoints before cart/checkout.
      id: normalizeProductId(product.id),
      // Ensure consistent name formatting
      name: product.name.trim(),
      price: product.price,
      image: product.image,
      category: product.category,
      description: product.description,
      status: product.status,
      // Cart-specific fields
      quantity: quantity,
      unit: product.unit || 'ea',
      effectiveUnitPrice: specialPricing.effectiveUnitPrice,
      originalPrice: product.price,
      hasSpecialPricing: specialPricing.hasSpecialPricing,
      specialPricingInfo: buildSpecialPricingInfo(product, quantity)
    };
    
    addToCart(cartProduct);
    
    // Enhanced notification message for special pricing
    let notificationMessage;
    if (specialPricing.type === 'egg-bulk') {
      const unitLabel = getUnitLabel(product.unit || 'dozen');
      notificationMessage = `${quantity} ${product.name} added to basket at $${specialPricing.bulkUnitPrice.toFixed(2)} / ${unitLabel} ($${specialPricing.totalPrice.toFixed(2)})`;
    } else if (specialPricing.type === 'corn-bulk') {
      notificationMessage = `${quantity} ${product.name} added to basket with corn bulk pricing ($${specialPricing.totalPrice.toFixed(2)})`;
    } else {
      // Updated: Include unit for lbs, omit for other units
      const unit = product.unit || 'ea';
      if (unit.toLowerCase() === 'lbs' || unit.toLowerCase() === 'lb') {
        notificationMessage = `${quantity} ${unit} of ${product.name} added to basket`;
      } else {
        notificationMessage = `${quantity} ${product.name} added to basket`;
      }
    }
    
    // Show notification using enhanced notification system
    showNotification(notificationMessage);
    
    // Reset the input mode and value
    setQuantityInputMode(prev => ({
      ...prev,
      [product.id]: false
    }));
    setQuantityValues(prev => ({
      ...prev,
      [product.id]: 1
    }));
  }, [addToCart, quantityInputMode, quantityValues, normalizeProductId, showNotification]);

  // Defer below-the-fold API requests until sections approach the viewport.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoadAnnouncements(true);
      setShouldLoadProducts(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          if (entry.target === announcementSectionRef.current) {
            setShouldLoadAnnouncements(true);
          }

          if (entry.target === featuredSectionRef.current) {
            setShouldLoadProducts(true);
          }

          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '300px 0px' }
    );

    if (announcementSectionRef.current) {
      observer.observe(announcementSectionRef.current);
    }
    if (featuredSectionRef.current) {
      observer.observe(featuredSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch announcements from API - UPDATED to not use mock data when no announcements
  useEffect(() => {
    if (window.location.hash !== '#announcements') return undefined;
    setShouldLoadAnnouncements(true);
    const frame = window.requestAnimationFrame(() => {
      announcementSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (!shouldLoadAnnouncements) {
      return undefined;
    }

    const fetchAnnouncements = async (retryCount = 0) => {
      try {
        setIsLoading(true);
        // Add timestamp to prevent caching and add limit parameter
        const announcementApi = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-announcements-get';
        const apiUrl = `${announcementApi}?limit=5&_=${Date.now()}`;
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch announcements: ${response.status}`);
        }
        
        const data = await response.json();
        // Check if the response is valid
        if (!Array.isArray(data)) {
          throw new Error('Empty response from announcements API');
        }

        const focusedId = new URLSearchParams(window.location.search).get('announcement');
        let visibleAnnouncements = data;
        if (focusedId && /^\d+$/.test(focusedId)
            && !data.some(item => String(item.id) === focusedId)) {
          const focusedResponse = await fetch(`${announcementApi}?id=${encodeURIComponent(focusedId)}`);
          if (focusedResponse.ok) {
            const focusedItems = await focusedResponse.json();
            if (Array.isArray(focusedItems) && focusedItems.length === 1) {
              visibleAnnouncements = [...data, focusedItems[0]];
            }
          }
        }
        
        // If data is empty or not an array, retry or use empty announcements
        if (visibleAnnouncements.length === 0) {
          if (retryCount < 2) {
            setTimeout(() => fetchAnnouncements(retryCount + 1), 1000);
            return;
          }
          // Set empty announcements array - no fallbacks
          setAnnouncements([]);
        } else {
          setAnnouncements(visibleAnnouncements);
          const focusedIndex = visibleAnnouncements.findIndex(item => String(item.id) === focusedId);
          if (focusedIndex >= 0) setCurrentAnnouncement(focusedIndex);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching announcements:", err);
        setError(err.message);
        
        // Retry logic for network errors
        if (retryCount < 2) {
          setTimeout(() => fetchAnnouncements(retryCount + 1), 1500);
          return;
        }
        
        // Set empty announcements in case of error
        setAnnouncements([]);
        setIsLoading(false);
      }
    };

    fetchAnnouncements();
    
    // Refresh announcements periodically (every 5 minutes)
    const announcementRefreshInterval = setInterval(() => {
      fetchAnnouncements();
    }, 300000); // 5 minutes in milliseconds
    
    return () => {
      clearInterval(announcementRefreshInterval);
    };
  }, [shouldLoadAnnouncements]);

  // UPDATED: Fetch seasonal products from API with CORS fix
  useEffect(() => {
    if (!shouldLoadProducts) {
      return undefined;
    }

    const fetchSeasonalProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-landing-page-inventory-get', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          // Remove cache-busting headers that cause CORS preflight issues
          // mode: 'cors', // This is the default, so we can omit it
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch seasonal products: ${response.status}`);
        }
        
        const data = await response.json();
        // Check if the response contains products and is in the expected format
        if (data && data.products && Array.isArray(data.products) && data.products.length > 0) {
          setSeasonalProducts(data.products);
        } else {
          // If no products are found, don't use fallback mock data
          setSeasonalProducts([]);
        }
      } catch (err) {
        console.error("Error fetching seasonal products:", err);
        setProductError(err.message);
        
        // Don't use fallback mock data, just set empty array
        setSeasonalProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchSeasonalProducts();
  }, [shouldLoadProducts]);

  // Animation on scroll and rotations for testimonials and announcements
  useEffect(() => {
    setIsVisible(true);
    
    // Testimonial rotation
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    // Announcement rotation - only start if announcements are loaded
    let announcementInterval;
    if (announcements.length > 0 && !new URLSearchParams(window.location.search).has('announcement')) {
      announcementInterval = setInterval(() => {
        setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
      }, 7000);
    }
    
    return () => {
      clearInterval(testimonialInterval);
      if (announcementInterval) clearInterval(announcementInterval);
    };
  }, [announcements.length, testimonials.length]);

  // Helper function to format date string - ENHANCED
  const formatAnnouncementDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let normalizedDateString = dateString;
      
      if (!normalizedDateString.includes('T') && !normalizedDateString.includes('Z')) {
        normalizedDateString = normalizedDateString.replace(' ', 'T');
      }
      
      const date = new Date(normalizedDateString);
      
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return '';
      }
      
      // Show relative time for recent dates
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        return 'Posted yesterday';
      } else if (diffDays < 7) {
        return `Posted ${diffDays} days ago`;
      } else {
        // Format options for older dates
        const options = { 
          month: 'long', 
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        };
        return `Posted ${date.toLocaleDateString('en-US', options)}`;
      }
    } catch (e) {
      console.error("Error formatting date", e, dateString);
      return '';
    }
  };

  // Handle newsletter subscription
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    // Reset message states
    setSubscribeMessage(null);
    setSubscribeError(null);
    
    // Validate email
    if (!email || !email.includes('@') || !email.includes('.')) {
      setSubscribeError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await newsletterFetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-email-subscriber-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name: name || undefined,
          company_url: newsletterCompanyUrl,
          form_started_at: newsletterFormStartedAt
        })
      });
      
      const data = await response.json();
      const emailDelivery = data?.email_delivery || null;
      const subscriberEmailSent = emailDelivery?.subscriber_email_sent
        ?? emailDelivery?.confirmation_email_sent;
      
      if (response.ok) {
        if (
          emailDelivery &&
          (subscriberEmailSent === false || emailDelivery.business_notification_sent === false)
        ) {
          setSubscribeMessage(
            `${data.message || 'Thank you for subscribing to our newsletter!'} Your signup is active, though the welcome email may be delayed.`
          );
        } else {
          setSubscribeMessage(data.message || 'Thank you for subscribing to our newsletter!');
        }
        setEmail(''); // Clear the email input on success
        setName(''); // Clear the name input on success
        setNewsletterCompanyUrl('');
        setNewsletterFormStartedAt(Date.now());
      } else {
        setSubscribeError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting newsletter form:', err);
      setSubscribeError('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to navigate to shop page
  const navigateToShop = () => {
    window.location.href = '/shop';
  };

  // Navigate to Buck's Box page
  const navigateToSubscription = () => {
    window.location.href = '/bucks-box';
  };

  // Navigate to About/Story page
  const navigateToStory = () => {
    window.location.href = '/about';
  };

  const handleOptimizedImageError = (event, fallbackSrc) => {
    const img = event.currentTarget;
    if (img.dataset.fallbackApplied === 'true') {
      return;
    }

    img.dataset.fallbackApplied = 'true';
    img.removeAttribute('srcset');
    img.src = fallbackSrc;
  };

  return (
    <div className="bucks-landing">
      {/* Enhanced Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-content">
          <h1>Taste Nature's Perfect Harvest, Delivered</h1>
          <p className="hero-subtitle">
            Experience the difference of locally grown, seasonal produce straight from our fertile land to your table — always fresh, always sustainable.
          </p>
          <div className="hero-cta-container">
            <button className="cta-primary" onClick={navigateToShop}>
              Explore the Harvest
            </button>
            <button className="cta-secondary" onClick={() => window.location.href = '/about'}>
              Our Story
            </button>
          </div>
          
          {/* Subtle testimonial preview - Ogilvy's social proof principle */}
          <div className="hero-testimonial-preview">
            <div className="rating-stars">★★★★★</div>
            <p>"10/10 quality, 10/10 service & kindness, 10/10 recommend."</p>
          </div>
        </div>
        
        <div className="hero-image-container">
          <div className="hero-decoration-circle"></div>
          <img
            src={coverPhotoDesktop}
            srcSet={`${coverPhotoMobile} 360w, ${coverPhotoDesktop} 720w`}
            sizes="(max-width: 768px) calc(100vw - 3rem), (max-width: 1024px) min(80vw, 720px), 44vw"
            alt="Fresh seasonal produce from Buck's Farmstand"
            className="hero-image"
            width="720"
            height="766"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={(event) => handleOptimizedImageError(event, coverPhotoFallback)}
          />
          <div className="hero-image-badge">
            <span>100%</span>
            <span>Local</span>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2>Why Choose Bucks Farmstand?</h2>
        <div className="benefits-container">
          <div className="benefit-card">
            <div className="benefit-icon">🌱</div>
            <h3>Always Fresh</h3>
            <p>Harvested daily, our produce reaches you at peak freshness and flavor.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🚜</div>
            <h3>Locally Grown</h3>
            <p>Support local agriculture and reduce environmental impact with our farm-to-table approach.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🧺</div>
            <h3>Farm-Direct Experience</h3>
            <p>Our farmstand offers a genuine connection to your food, with hand-selected produce straight from local fields to your table.</p>
          </div>
        </div>
      </section>

      {/* Announcement Board Section */}
      <section className="announcement-section" id="announcements" ref={announcementSectionRef}>
        <h2>From the Buck's Board</h2>
        <p className="announcement-subtitle">
          Stay updated with what's happening at our farmstand
        </p>
        <div className="announcement-board">
          {!shouldLoadAnnouncements ? (
            <div className="announcement-loading">Latest updates will load as you scroll.</div>
          ) : isLoading ? (
            <div className="announcement-loading">Loading announcements...</div>
          ) : error ? (
            <div className="announcement-error">
              <p>Sorry, we couldn't load our latest announcements.</p>
              <p>Please check back later!</p>
            </div>
          ) : announcements.length > 0 ? (
            <div className="announcement-card">
              <div className="announcement-header">
                <div className="announcement-tag-container">
                  <div 
                    className="announcement-tag" 
                    data-tag={announcements[currentAnnouncement].tag}
                  >
                    {announcements[currentAnnouncement].tag || 'Update'}
                  </div>
                  <div className="announcement-date">
                    {formatAnnouncementDate(announcements[currentAnnouncement].publish_start)}
                  </div>
                </div>
              </div>
              
              <h3 className="announcement-title">{announcements[currentAnnouncement].title}</h3>
              
              {/* Display summary as a subtitle if it exists and is different from body */}
              {announcements[currentAnnouncement].summary && 
               announcements[currentAnnouncement].summary !== announcements[currentAnnouncement].body && (
                <p className="announcement-summary">{announcements[currentAnnouncement].summary}</p>
              )}
              
              {/* Display the full body content */}
              <div className="announcement-body">
                {announcements[currentAnnouncement].body ? (
                  <div dangerouslySetInnerHTML={{ 
                    __html: announcements[currentAnnouncement].body.replace(/\n/g, '<br />') 
                  }} />
                ) : (
                  <p>{announcements[currentAnnouncement].summary}</p>
                )}
              </div>
              
              {/* Image display */}
              {announcements[currentAnnouncement].image_url && 
               announcements[currentAnnouncement].image_url !== '' && (
                <div className="announcement-image-container">
                  <img 
                    src={announcements[currentAnnouncement].image_url} 
                    alt={announcements[currentAnnouncement].title}
                    className="announcement-image"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="announcement-empty">
              <div className="announcement-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="120" height="120">
                  {/* Farm bulletin board icon */}
                  <path d="M448 64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h384c35.35 0 64-28.65 64-64V128C512 92.65 483.3 64 448 64z" fill="#a67c52" opacity="0.1"/>
                  <path d="M448 80H64C37.49 80 16 101.5 16 128v256c0 26.51 21.49 48 48 48h384c26.51 0 48-21.49 48-48V128C496 101.5 474.5 80 448 80zM64 112h384c8.837 0 16 7.163 16 16v32H48v-32C48 119.2 55.16 112 64 112zM448 400H64c-8.837 0-16-7.163-16-16V192h416v192C464 392.8 456.8 400 448 400z" fill="#a67c52"/>
                  {/* Empty note/paper in the middle */}
                  <path d="M120 240h272c4.418 0 8 3.582 8 8v112c0 4.418-3.582 8-8 8H120c-4.418 0-8-3.582-8-8V248c0-4.418 3.582-8 8-8z" fill="#f5f0e5"/>
                  {/* Notice pin */}
                  <circle cx="256" cy="232" r="12" fill="#a67c52"/>
                  {/* Decorative lines for paper */}
                  <path d="M140 280h232M140 320h232" stroke="#d6bf9d" strokeWidth="4" strokeLinecap="round"/>
                  {/* Wheat stalks for farm theme */}
                  <path d="M104 64c0 0-8-16-24-16s-24 16-24 16M80 64v48M56 96h48M80 120c0 0 8 16 24 16s24-16 24-16" stroke="#a67c52" strokeWidth="3" strokeLinecap="round"/>
                  <path d="M424 64c0 0-8-16-24-16s-24 16-24 16M400 64v48M376 96h48M400 120c0 0 8 16 24 16s24-16 24-16" stroke="#a67c52" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No News from the Stand Today</h3>
                <p>We don't have any new updates to share right now, but the stand is still stocked with fresh finds.</p>
                <p>Check back soon for updates on new arrivals, special offerings, and community events at the farmstand!</p>
            </div>
          )}
          
          {/* Only show navigation dots if we have multiple announcements */}
          {!isLoading && !error && announcements.length > 1 && (
            <div className="announcement-nav">
              {announcements.map((_, index) => (
                <button 
                  key={index} 
                  className={`announcement-dot ${index === currentAnnouncement ? 'active' : ''}`}
                  onClick={() => setCurrentAnnouncement(index)}
                  aria-label={`Announcement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section - Enhanced with elegant farmstand styling */}
      <section className="featured-section" ref={featuredSectionRef}>
        <div className="section-header">
          <span className="section-accent-line"></span>
          <h2>This Season's Harvest</h2>
          <p className="section-subtitle">Fresh from our fields to your table</p>
        </div>
        <div className="landing-inventory-showcase">
          {!shouldLoadProducts ? (
            <div className="landing-inventory-loading">
              <p>Fresh inventory loads as you scroll.</p>
            </div>
          ) : isLoadingProducts ? (
            // Enhanced loading state for products
            <div className="landing-inventory-loading">
              <div className="loading-spinner"></div>
              <p>Harvesting fresh produce...</p>
            </div>
          ) : productError ? (
            // Enhanced error state for products
            <div className="landing-inventory-error">
              <div className="error-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#b25d5d" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12" y2="16" />
                </svg>
              </div>
              <h3>We're having trouble loading our products</h3>
              <p>Please check back soon as we refresh our inventory. Meanwhile, you can visit our farmstand in person!</p>
            </div>
          ) : seasonalProducts.length > 0 ? (
            // Successfully loaded products with enhanced elegant card design
            seasonalProducts.map((product) => (
              <div
                className={`landing-inventory-card ${isProductOutOfStock(product) ? 'out-of-stock-card' : ''}`}
                key={product.id || product.name}
              >
                {/* Decorative corner accent */}
                <div className="card-corner-accent"></div>

                {/* Image container with overlay and category badge */}
                <div className="landing-inventory-image-wrapper">
                  {isProductOutOfStock(product) && (
                    <div className="landing-stock-ribbon" aria-label="Out of stock">
                      Out of Stock
                    </div>
                  )}
                  <img
                    src={product.image || '/placeholder-product.png'}
                    alt={product.name}
                    className="landing-inventory-image"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      // Prevent infinite loop by checking if already set to fallback
                      if (!e.target.dataset.fallbackApplied) {
                        e.target.dataset.fallbackApplied = 'true';
                        e.target.src = "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=400&h=300&fit=crop&q=80";
                      }
                    }}
                  />
                  <div className="image-overlay"></div>
                  {product.category && (
                    <span className="product-category-badge">{product.category}</span>
                  )}
                </div>

                <div className="landing-inventory-content">
                  {/* Product title with subtle underline */}
                  <div className="product-title-wrapper">
                    <h3 className="landing-inventory-title">{product.name}</h3>
                    <span className="title-underline"></span>
                  </div>

                  <p className="landing-inventory-description">{product.description}</p>

                  {/* Enhanced price display */}
                  <div className="landing-inventory-price-wrapper">
                    <span className="price-amount">${product.price.toFixed(2)}</span>
                    {product.unit && <span className="price-unit">/ {product.unit}</span>}
                  </div>

                  {/* Quantity Input Mode */}
                  {quantityInputMode[product.id] ? (
                    <div className="quantity-input-container">
                      <div className="quantity-input-wrapper">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={quantityValues[product.id] === '' ? '' : (quantityValues[product.id] || 1)}
                          onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                          onBlur={() => handleQuantityBlur(product.id)}
                          onFocus={handleQuantityFocus}
                          className="quantity-input"
                          placeholder="1"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                        <span className="quantity-unit">{product.unit || 'ea'}</span>
                      </div>

                      {/* Special Pricing Preview */}
                      {(() => {
                        const qty = quantityValues[product.id] || 1;
                        if (qty === '' || qty < 1) return null;

                        const pricing = calculateSpecialPricing(product, qty);
                        if (!pricing.hasSpecialPricing) return null;

                        return (
                        <div className="special-pricing-preview">
                          <div className="pricing-breakdown">
                            {pricing.type === 'egg-bulk' ? (
                              <div className="dozen-pricing">
                                {qty} {getUnitLabel(product.unit || 'dozen')} @ ${pricing.bulkUnitPrice.toFixed(2)} = ${pricing.totalPrice.toFixed(2)}
                              </div>
                            ) : (
                              <>
                                {pricing.dozens > 0 && (
                                  <div className="dozen-pricing">
                                    {pricing.dozens} dozen @ ${pricing.dozenPrice.toFixed(2)} = ${(pricing.dozens * pricing.dozenPrice).toFixed(2)}
                                  </div>
                                )}
                                {pricing.halfDozens > 0 && (
                                  <div className="dozen-pricing">
                                    Half dozen @ ${pricing.halfDozenPrice.toFixed(2)} = ${(pricing.halfDozens * pricing.halfDozenPrice).toFixed(2)}
                                  </div>
                                )}
                                {pricing.remainder > 0 && (
                                  <div className="remainder-pricing">
                                    + {pricing.remainder} ea @ ${pricing.originalUnitPrice.toFixed(2)} = ${(pricing.remainder * pricing.originalUnitPrice).toFixed(2)}
                                  </div>
                                )}
                              </>
                            )}
                            <div className="total-pricing">
                              <strong>Total: ${pricing.totalPrice.toFixed(2)}</strong>
                              <span className="savings"> (Save ${pricing.totalSavings.toFixed(2)})</span>
                            </div>
                          </div>
                        </div>
                        );
                      })()}

                      <div className="quantity-actions">
                        <button
                          className="confirm-add-btn"
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={isProductOutOfStock(product)}
                        >
                          <span className="btn-icon">+</span>
                          Add to Basket
                        </button>
                        <button
                          className="cancel-quantity-btn"
                          onClick={(e) => handleCancelQuantityInput(product.id, e)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Enhanced Add to Cart Button */
                    <button
                      className="landing-inventory-cta"
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={isProductOutOfStock(product)}
                    >
                      <span className="btn-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <path d="M16 10a4 4 0 0 1-8 0"></path>
                        </svg>
                      </span>
                      <span className="btn-text">{isProductOutOfStock(product) ? 'Out of Stock' : 'Add to Basket'}</span>
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            // Enhanced empty state for products
            <div className="landing-inventory-empty">
              <div className="product-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80">
                  <circle cx="40" cy="40" r="38" fill="#f5f0e5" stroke="#d6bf9d" strokeWidth="2"/>
                  <path d="M25 50 Q40 60 55 50" stroke="#a67c52" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  <circle cx="28" cy="35" r="3" fill="#a67c52"/>
                  <circle cx="52" cy="35" r="3" fill="#a67c52"/>
                  <path d="M20 20 Q30 15 40 20" stroke="#8bc34a" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  <path d="M40 20 Q50 15 60 20" stroke="#8bc34a" strokeWidth="2" fill="none" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Taking a brief harvest break</h3>
              <p>We're currently between harvests or preparing new seasonal offerings.</p>
              <p>Check back soon for fresh picks from our fields, or visit us in person to see what's available today!</p>
              <button
                className="cta-secondary visit-btn"
                onClick={() => window.location.href = '/about#location'}
              >
                Find Our Location
              </button>
            </div>
          )}
        </div>
        {seasonalProducts.length > 0 && (
          <div className="view-all-container">
            <button className="view-all-btn" onClick={navigateToShop}>
              <span>View All Products</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          </div>
        )}
      </section>

     {/* Subscription Section */}
      <section className="subscription-section">
        <div className="subscription-content">
          <h2>The Buck's Box</h2>
          <p>
            Get a curated box of seasonal produce delivered weekly or monthly. Experience the best of what our farm has to offer, right at your doorstep.
          </p>
          <ul className="subscription-benefits">
            <li>Always seasonal, always fresh</li>
            <li>Convenient delivery options</li>
            <li>Support sustainable farming</li>
            <li>Discover new varieties</li>
          </ul>
          <button className="cta-primary" onClick={navigateToSubscription}>Subscribe Now</button>
        </div>
        <div className="subscription-image">
          <img
            src={bucksBoxImageDesktop}
            srcSet={`${bucksBoxImageMobile} 360w, ${bucksBoxImageDesktop} 720w`}
            sizes="(max-width: 1024px) calc(100vw - 3rem), 42vw"
            alt="Buck's Box Subscription"
            width="1024"
            height="998"
            loading="lazy"
            decoding="async"
            onError={(event) => handleOptimizedImageError(event, bucksBoxImageFallback)}
          />
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <h2>What Our Customers Say</h2>
        <div className="testimonial-carousel">
          <div className="testimonial-card">
            <p className="testimonial-quote">"{testimonials[currentTestimonial].quote}"</p>
            <p className="testimonial-author">- {testimonials[currentTestimonial].author}</p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="newsletter-section" id="newsletter">
        <div className="newsletter-container">
          <h2>Stay Connected</h2>
          <p>
            Sign up for our newsletter to receive updates on seasonal offerings, special promotions, and farm events.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="newsletter-form-container">
            <div className="newsletter-honeypot-field" aria-hidden="true">
              <label htmlFor="newsletter-company-url">Company website</label>
              <input
                id="newsletter-company-url"
                type="text"
                name="company_url"
                value={newsletterCompanyUrl}
                onChange={(e) => setNewsletterCompanyUrl(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="newsletter-input" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button 
                type="submit" 
                className="newsletter-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
            
            {/* Message display */}
            {subscribeMessage && (
              <div className="newsletter-success">
                {subscribeMessage}
              </div>
            )}
            
            {subscribeError && (
              <div className="newsletter-error">
                {subscribeError}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Farm Story Section */}
      <section className="farm-story-section">
        <div className="farm-story-image">
          <img
            src={bucksStoryImageDesktop}
            srcSet={`${bucksStoryImageMobile} 360w, ${bucksStoryImageDesktop} 500w`}
            sizes="(max-width: 1024px) calc(100vw - 3rem), 40vw"
            alt="Our Farm Story"
            width="500"
            height="333"
            loading="lazy"
            decoding="async"
            onError={(event) => handleOptimizedImageError(event, bucksStoryImageFallback)}
          />
        </div>
        <div className="farm-story-content">
          <h2>Our Story</h2>
          <p>
            Nestled in Alsip, Illinois, Buck's Farmstand has been a family-run gathering spot for fresh, local flavors for over three generations. We partner with nearby farms for seasonal produce, raise our own hens for pasture-raised eggs, and craft small-batch apple sauce and artisan breads right here in town.
          </p>
          <p>
            Every item on our stand is a celebration of community, carefully curated to honor sustainable practices and the hard work of local farms. When you shop with us, you're supporting the land and neighbors who make Buck's Farmstand possible.
          </p>
          <button className="cta-secondary" onClick={navigateToStory}>
            Discover Our Roots
          </button>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="final-cta-section">
        <h2>Bring Home Buck&apos;s Best</h2>
        <p>
          Hand-picked local produce and farmstand favorites, prepared with care and ready for your table.
        </p>
        <button className="cta-primary cta-large" onClick={navigateToShop}>Start Shopping</button>
      </section>

      {/* Enhanced Cart Notification to match shop page */}
      <div id="cart-notification" className="cart-notification">
        Item added to basket
      </div>


      {/* Enhanced Hero Section Styles */}
      <style>
        {`
          /* Enhanced Hero Section with Ogilvy principles and Apple aesthetics */

          /* Hero Section Base - refined gradients and animations */
          .hero-section {
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 90vh;
            padding: 8rem 6rem;
            background: linear-gradient(135deg, rgba(255, 252, 245, 0.97), rgba(245, 240, 225, 0.93));
            position: relative;
            overflow: hidden;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 1.2s ease, transform 1.2s ease;
          }

          /* Create subtle organic patterns for texture like Apple designs */
          .hero-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23a67c52' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
            pointer-events: none;
          }

          .hero-section.visible {
            opacity: 1;
            transform: translateY(0);
          }

          /* Headline refinement - Ogilvy emphasized strong headlines */
          .hero-content h1 {
            font-size: 3.8rem;
            line-height: 1.2;
            letter-spacing: -0.02em;
            background: linear-gradient(to right, #6b4c35, #a67c52);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 1.5rem;
            position: relative;
            font-weight: 800;
            max-width: 600px;
          }

          /* Subtle underline for headline - Apple-like understated details */
          .hero-content h1::after {
            content: "";
            position: absolute;
            bottom: -10px;
            left: 0;
            width: 100px;
            height: 3px;
            background: #d6bf9d;
            border-radius: 3px;
          }

          /* Subtitle enhancement - Apple's clear, precise language */
          .hero-subtitle {
            font-size: 1.5rem;
            line-height: 1.6;
            color: #6b5a44;
            margin-bottom: 2.5rem;
            font-weight: 300;
            max-width: 540px;
          }

          /* Content area refinement */
          .hero-content {
            flex: 1;
            padding-right: 5rem;
            position: relative;
            z-index: 2;
          }

          /* Image container refinement */
          .hero-image-container {
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            z-index: 1;
          }

          /* Apple-like image treatment */
          .hero-image {
            max-width: 100%;
            border-radius: 24px;
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
            transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
            transform-origin: center bottom;
            margin-bottom: 30px;
          }

          .hero-image:hover {
            transform: scale(1.03) translateY(-10px);
            box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2);
          }

          /* Ogilvy-inspired CTA section with clarity and focus */
          .hero-cta-container {
            margin-top: 3rem;
            display: flex;
            align-items: center;
          }

          /* Primary CTA enhancement with Apple-like simplicity */
          .cta-primary {
            background-color: #a67c52;
            color: white;
            font-weight: 600;
            padding: 1rem 2.2rem;
            border-radius: 50px;
            font-size: 1.1rem;
            letter-spacing: 0.02em;
            transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
            position: relative;
            overflow: hidden;
            z-index: 1;
          }

          .cta-primary:hover {
            background-color: #8c6544;
            transform: translateY(-3px) scale(1.02);
            box-shadow: 0 10px 25px rgba(138, 97, 66, 0.25);
          }

          /* Ogilvy-inspired secondary action with less prominence */
          .cta-secondary {
            background-color: transparent;
            color: #a67c52;
            border: 2px solid #a67c52;
            margin-left: 1.5rem;
            font-weight: 600;
            letter-spacing: 0.02em;
            transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
          }

          .cta-secondary:hover {
            background-color: rgba(166, 124, 82, 0.08);
            transform: translateY(-3px);
            box-shadow: 0 10px 25px rgba(138, 97, 66, 0.15);
          }

          /* Add subtle decoration elements for visual interest */
          .hero-content::before {
            content: "";
            position: absolute;
            top: -80px;
            left: -60px;
            width: 150px;
            height: 150px;
            background: radial-gradient(circle, rgba(166, 124, 82, 0.08) 0%, rgba(166, 124, 82, 0) 70%);
            border-radius: 50%;
            z-index: -1;
          }

          /* Hero testimonial preview */
          .hero-testimonial-preview {
            margin-top: 2rem;
            display: flex;
            flex-direction: column;
            max-width: 350px;
            opacity: 0.8;
            transition: all 0.3s ease;
            border-left: 2px solid rgba(166, 124, 82, 0.3);
            padding-left: 1rem;
            margin-left: 0.5rem;
          }
          
          .hero-testimonial-preview:hover {
            opacity: 1;
          }
          
          .rating-stars {
            color: #a67c52;
            letter-spacing: 2px;
            margin-bottom: 0.3rem;
            font-size: 0.9rem;
          }
          
          .hero-testimonial-preview p {
            font-style: italic;
            font-size: 0.95rem;
            margin: 0;
            color: #6b5a44;
          }
          
          .hero-decoration-circle {
            position: absolute;
            width: 250px;
            height: 250px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(214, 191, 157, 0.1) 0%, rgba(214, 191, 157, 0) 70%);
            top: -50px;
            right: -100px;
            z-index: -1;
            animation: float 6s ease-in-out infinite;
          }
          
          .hero-image-badge {
            position: absolute;
            bottom: 30px;
            right: -15px;
            background: #a67c52;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: white;
            font-weight: 700;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
            transform: rotate(-8deg);
            animation: float 5s ease-in-out infinite;
          }
          
          .hero-image-badge span:first-child {
            font-size: 1.4rem;
            line-height: 1;
          }
          
          .hero-image-badge span:last-child {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          
          /* Subtle floating animation for decorative elements - Apple-like attention to motion */
          @keyframes float {
            0% { transform: translateY(0px) rotate(-8deg); }
            50% { transform: translateY(-15px) rotate(-8deg); }
            100% { transform: translateY(0px) rotate(-8deg); }
          }

          /* Media queries for responsive design */
          @media (max-width: 1280px) {
            .hero-section {
              padding: 6rem 4rem;
            }
            
            .hero-content h1 {
              font-size: 3.2rem;
            }
          }

          @media (max-width: 1024px) {
            .hero-section {
              flex-direction: column;
              padding: 5rem 3rem;
              text-align: center;
            }
            
            .hero-content {
              padding-right: 0;
              margin-bottom: 3rem;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            
            .hero-content h1 {
              font-size: 3rem;
            }
            
            .hero-content h1::after {
              left: 50%;
              transform: translateX(-50%);
            }
            
            .hero-subtitle {
              max-width: 100%;
            }
            
            .hero-testimonial-preview {
              margin: 2rem auto 0;
              border-left: none;
              border-top: 2px solid rgba(166, 124, 82, 0.3);
              padding-left: 0;
              padding-top: 1rem;
              text-align: center;
            }
            
            .hero-image-badge {
              right: 50%;
              transform: translateX(80px) rotate(-8deg);
            }
          }

          @media (max-width: 768px) {
            .hero-section {
              padding: 4rem 2rem;
            }
            
            .hero-content h1 {
              font-size: 2.5rem;
            }
            
            .hero-subtitle {
              font-size: 1.2rem;
            }
            
            .hero-cta-container {
              flex-direction: column;
              width: 100%;
            }
            
            .cta-primary, .cta-secondary {
              width: 100%;
              margin: 0.5rem 0;
            }
            
            .cta-secondary {
              margin-left: 0;
            }
          }

          @media (max-width: 375px) {
            .hero-section {
              min-height: auto;
              padding: 3rem 1.25rem 1.75rem;
            }

            .hero-content {
              margin-bottom: 1.5rem;
            }

            .hero-image {
              margin-bottom: 0;
            }

            .hero-image-badge {
              right: 0.75rem;
              bottom: 0.75rem;
              width: 72px;
              height: 72px;
              transform: rotate(-8deg);
            }

            .hero-image-badge span:first-child {
              font-size: 1.2rem;
            }

            .hero-image-badge span:last-child {
              font-size: 0.75rem;
            }

            .benefits-section {
              padding-top: 3.5rem;
            }
          }
        `}
      </style>
    </div>
  );
}

export default BucksLanding;
